import React, { FC, useId, useState } from 'react';

import ComposeFooterContainer from '../../containers/compose/composeFooter';
import useComposeType from '../../hooks/router/search/useComposeType';
import useIsMobile from '../../hooks/useIsMobile';
import translate from '../../i18n/translate';

import ComposeMain from './Common/ComposeMain';
import ComposeMeta from './Common/ComposeMeta';
import ComposeModal from './ComposeModal';
import { getComposeTitle } from './helpers';

import useShouldShowCompose from './hooks/useShouldShowCompose';
import Props from './props';
import * as Styled from './styled';

/**
 * Main compose block,
 * this component wraps all the functionality there
 *
 * @param props                       The props passed to the component
 * @param props.isDraftChanged        Whether the draft has been changed
 * @param props.isInitialDraftChanged Whether the initial draft has been changed
 * @param props.isPending             Whether the mutation is in progress
 * @param props.nodeId                The ID of the node (template) being edited (null for creation)
 * @param props.nodeType              Whether the compose form is being used for messages or templates
 * @param props.requestClear          Request clearing the compose form
 * @param props.requestClose          Request closing the compose form
 * @param props.requestSubmit         Request that the message/template is created/updated
 *
 * @returns                           The component itself
 */
const Compose: FC<Props> = ({
  isDraftChanged,
  isInitialDraftChanged,
  isPending,
  nodeId,
  nodeType,
  requestClear,
  requestClose,
  requestSubmit,
}) => {
  const [shouldValidate, setShouldValidate] = useState(false);
  const composeType = useComposeType();
  /**
   * Form ID is needed here because we have 2 buttons that submit this form.
   * One is external in the compose header so we need to link to the form with it.
   */
  const formId = useId();
  const isMobile = useIsMobile();
  const shouldShow = useShouldShowCompose(isDraftChanged);

  // We render submit button at different HTML points depending on screen size,
  // This covers props used in both places
  const mainProps = {
    formId,
    isInitialDraftChanged,
    nodeType,
    requestClear,
    requestSubmit,
  };

  const composeTitle = getComposeTitle(composeType, nodeId, nodeType);

  if (!shouldShow) {
    return null;
  }

  if (isMobile) {
    return (
      <Styled.Wrapper aria-label={translate('ARIA__LABEL__COMPOSE')}>
        <Styled.InnerWrapper>
          <Styled.Content>
            <ComposeMain {...mainProps} shouldShowTitle={true} />

            <ComposeMeta formId={formId} nodeType={nodeType} position="BELOW" />
          </Styled.Content>

          <ComposeFooterContainer
            formId={formId}
            isDraftChanged={false}
            isPending={isPending}
            nodeType={nodeType}
          />
        </Styled.InnerWrapper>
      </Styled.Wrapper>
    );
  }

  return (
    <ComposeModal
      {...mainProps}
      isDraftChanged={isDraftChanged}
      isPending={isPending}
      requestClose={requestClose}
      setShouldValidate={setShouldValidate}
      shouldValidate={shouldValidate}
      title={composeTitle}
    />
  );
};

export default Compose;
